import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ClipboardList, Search, ArrowLeft, Mail, Phone, DollarSign, Clock,
  CheckCircle2, Send, CalendarClock, Video, Sparkles, Plus, Pencil, Trash2,
} from "lucide-react";

const STATUS_STEPS = ["paid", "customer_proposed", "admin_counter", "confirmed"];

export default function IntakesAdminTab() {
  const [rows, setRows] = useState([]);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const [rRes, qRes] = await Promise.all([
        api.get("/admin/intake/responses"),
        api.get("/admin/intake/questions"),
      ]);
      setRows(rRes.data);
      setSchema(qRes.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load intakes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/intake/responses/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load intake");
    }
  };

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.name || "").toLowerCase().includes(s)
      || (r.email || "").toLowerCase().includes(s)
      || (r.phone || "").toLowerCase().includes(s);
  });

  if (detail) {
    return <IntakeDetail
      intake={detail}
      schema={schema}
      onBack={() => { setDetail(null); load(); }}
      onRefresh={async () => {
        const { data } = await api.get(`/admin/intake/responses/${detail.intake_id}`);
        setDetail(data);
        load();
      }}
    />;
  }

  return (
    <div className="space-y-5" data-testid="intakes-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-white/85">
            <ClipboardList size={18} className="text-[#FF8A00]" />
            <span className="font-display text-xl">Session intakes</span>
          </div>
          <p className="text-sm text-white/50 mt-1">
            Every questionnaire submitted from the public booking page. Send the GHL scheduling link, then approve the slot + Zoom URL.
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="intakes-search"
            placeholder="Search name, email, phone"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 w-72 bg-black/40 border-white/10"
          />
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-white/60 text-[11px] tracking-[0.15em] uppercase">
            <tr>
              <th className="text-left px-4 py-3">Member</th>
              <th className="text-left px-4 py-3">Session</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Received</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-white/50">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-white/50">
                <ClipboardList className="mx-auto mb-3 text-white/30" size={24}/>
                No intakes yet — they'll appear here the moment someone books.
              </td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.intake_id}
                    onClick={() => openDetail(r.intake_id)}
                    data-testid={`intake-row-${r.intake_id}`}
                    className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer">
                  <td className="px-4 py-3">
                    <div>{r.name}</div>
                    <div className="text-xs text-white/45">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 text-white/80">
                    <div>{r.session_type_title}</div>
                    <div className="text-xs text-white/45">${(r.price_cents / 100).toFixed(0)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-3 text-white/55 text-xs">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-white/45">Open →</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    paid: { label: "Paid", classes: "bg-[#FF8A00]/10 text-[#FF8A00] border-[#FF8A00]/30", icon: <CheckCircle2 size={12}/> },
    pending_payment: { label: "Pending payment", classes: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30", icon: <Clock size={12}/> },
    customer_proposed: { label: "Client proposed", classes: "bg-purple-500/10 text-purple-300 border-purple-500/30", icon: <CalendarClock size={12}/> },
    admin_counter: { label: "You countered", classes: "bg-blue-500/10 text-blue-300 border-blue-500/30", icon: <Send size={12}/> },
    // legacy statuses (older intakes) — surfaced so nothing crashes
    link_sent: { label: "Link sent (legacy)", classes: "bg-white/5 text-white/50 border-white/10", icon: <Send size={12}/> },
    slot_requested: { label: "Slot requested (legacy)", classes: "bg-white/5 text-white/50 border-white/10", icon: <CalendarClock size={12}/> },
    confirmed: { label: "Confirmed", classes: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", icon: <CheckCircle2 size={12}/> },
  }[status] || { label: status || "—", classes: "bg-white/5 text-white/50 border-white/10", icon: null };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${cfg.classes}`}
      data-testid={`intake-status-${status || 'na'}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function StatusStepper({ status }) {
  const currentIdx = STATUS_STEPS.indexOf(status);
  const idx = currentIdx < 0 ? -1 : currentIdx;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="intake-stepper">
      {STATUS_STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full border ${
            i <= idx
              ? "bg-[#FF8A00]/15 text-[#FF8A00] border-[#FF8A00]/40"
              : "bg-white/5 text-white/40 border-white/10"
          }`}>
            {s.replace("_", " ")}
          </div>
          {i < STATUS_STEPS.length - 1 && (
            <div className={`h-px w-6 ${i < idx ? "bg-[#FF8A00]/60" : "bg-white/10"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function IntakeDetail({ intake, schema, onBack, onRefresh }) {
  const [ghlUrl, setGhlUrl] = useState("");
  const [ghlSet, setGhlSet] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setGhlUrl(data.ghl_calendar_url || "");
        setGhlSet(!!(data.ghl_calendar_url || "").trim());
      } catch { /* ignore */ }
    })();
  }, []);

  const questions = schema?.filter((q) => q.section && intake.answers && intake.answers[q.key] !== undefined) || [];
  const sections = [...new Set(questions.map((q) => q.section))];
  const answered = questions.length;

  return (
    <div className="space-y-5" data-testid="intake-detail">
      <button
        onClick={onBack}
        data-testid="intakes-back"
        className="text-sm text-white/60 hover:text-white flex items-center gap-2"
      >
        <ArrowLeft size={14} /> All intakes
      </button>

      <div className="glass rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center text-lg font-semibold">
            {(intake.name || "?")[0].toUpperCase()}
          </div>
          <div>
            <div className="font-display text-2xl">{intake.name}</div>
            <div className="flex items-center gap-4 text-sm text-white/60 mt-1">
              <a href={`mailto:${intake.email}`} className="flex items-center gap-1.5 hover:text-white"><Mail size={12}/> {intake.email}</a>
              <span className="flex items-center gap-1.5"><Phone size={12}/> {intake.phone}</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">{intake.session_type_title}</div>
          <div className="font-display text-2xl mt-1 flex items-center gap-1 justify-end">
            <DollarSign size={16} className="text-white/50"/>
            {(intake.price_cents / 100).toFixed(0)}
          </div>
          <div className="mt-1"><StatusBadge status={intake.status} /></div>
        </div>
      </div>

      {intake.status !== "pending_payment" && (
        <AdminBookingPipeline intake={intake} onRefresh={onRefresh} />
      )}

      <div className="text-xs text-white/40">Answered {answered} of {schema?.length || 0} questions.</div>

      {sections.map((s) => (
        <div key={s} className="glass rounded-2xl p-5 border border-white/6">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00] mb-4">{s}</div>
          <div className="space-y-4">
            {questions.filter((q) => q.section === s).map((q) => {
              const val = intake.answers[q.key];
              return (
                <div key={q.key} className="border-t border-white/5 pt-3 first:border-t-0 first:pt-0">
                  <div className="text-sm text-white/60">{q.label}</div>
                  <div className="text-white/90 mt-1 whitespace-pre-wrap">
                    {formatValue(val)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {intake.signature && (
        <div className="glass rounded-2xl p-5 border border-white/6">
          <div className="text-[11px] tracking-[0.22em] uppercase text-white/45 mb-2">Signature</div>
          <div className="font-serif text-2xl italic text-white/80">{intake.signature}</div>
        </div>
      )}

      <NotesSection intake={intake} onDone={onRefresh} />
    </div>
  );
}

function AdminBookingPipeline({ intake, onRefresh }) {
  const current = intake.current_proposal || null;
  const [saving, setSaving] = useState("");
  const [counterDt, setCounterDt] = useState("");
  const [counterDuration, setCounterDuration] = useState(current?.duration_min || intake.session_type_duration_min || 30);
  const [counterNote, setCounterNote] = useState("");
  const [zoom, setZoom] = useState(intake.meeting_url || "");
  const [adminNote, setAdminNote] = useState(intake.admin_note || "");
  const [zoomStatus, setZoomStatus] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/zoom/status");
        setZoomStatus(data);
      } catch { /* not fatal */ }
    })();
  }, []);

  const acceptClientProposal = async () => {
    setSaving("accept");
    try {
      const { data } = await api.post(
        `/admin/intake/responses/${intake.intake_id}/accept-proposal`,
        { zoom_url: zoom.trim(), admin_note: adminNote.trim() },
      );
      if (data.meeting_url && !zoom) setZoom(data.meeting_url);
      toast.success("Confirmed — customer emailed");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to confirm");
    } finally { setSaving(""); }
  };

  const sendCounter = async () => {
    if (!counterDt) { toast.error("Pick a date/time to propose"); return; }
    setSaving("counter");
    try {
      await api.post(
        `/admin/intake/responses/${intake.intake_id}/counter-propose`,
        {
          scheduled_at: new Date(counterDt).toISOString(),
          duration_min: parseInt(counterDuration, 10) || 30,
          note: counterNote.trim(),
        },
      );
      toast.success("Counter-proposal sent — customer emailed");
      setCounterDt(""); setCounterNote("");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to send");
    } finally { setSaving(""); }
  };

  const attachZoomOnly = async () => {
    // For confirmed bookings — admin just updates the Zoom URL
    if (!zoom.trim()) { toast.error("Paste a Zoom URL"); return; }
    setSaving("zoom");
    try {
      await api.post(
        `/admin/intake/responses/${intake.intake_id}/accept-proposal`,
        { zoom_url: zoom.trim(), admin_note: adminNote.trim() },
      ).catch(async () => {
        // Already confirmed — call confirm to re-email with new zoom
        await api.post(`/admin/intake/responses/${intake.intake_id}/confirm`, {
          scheduled_at: intake.scheduled_at,
          duration_min: intake.scheduled_duration_min || 30,
          zoom_url: zoom.trim(),
          admin_note: adminNote.trim(),
        });
      });
      toast.success("Zoom link attached and emailed");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    } finally { setSaving(""); }
  };

  const generateZoom = async () => {
    const startAt = intake.scheduled_at
      || (current?.proposed_by === "customer" ? current.scheduled_at : null);
    if (!startAt) { toast.error("Accept the customer's time first, then generate the Zoom link."); return; }
    setSaving("zoom-gen");
    try {
      const { data } = await api.post(
        `/admin/intake/responses/${intake.intake_id}/zoom/generate`,
        {
          scheduled_at: startAt,
          duration_min: intake.scheduled_duration_min || current?.duration_min || 30,
          topic: `${intake.session_type_title} — ${intake.name || ""}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        },
      );
      setZoom(data.join_url || "");
      toast.success(data.source === "zoom_api"
        ? "Zoom meeting created — customer gets a Zoom invite"
        : "Filled with your default meeting URL");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    } finally { setSaving(""); }
  };

  const isConfirmed = intake.status === "confirmed";
  const clientProposedNow = intake.status === "customer_proposed" && current?.proposed_by === "customer";
  const adminCounteredNow = intake.status === "admin_counter" && current?.proposed_by === "admin";
  const nothingYet = intake.status === "paid" && !current;

  return (
    <div className="glass rounded-2xl p-6 space-y-5 border border-[#FF8A00]/15" data-testid="booking-actions-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#FF8A00]/15 text-[#FF8A00] flex items-center justify-center">
          <CalendarClock size={18} />
        </div>
        <div className="font-display text-lg">Booking pipeline</div>
      </div>
      <StatusStepper status={intake.status} />

      {nothingYet && (
        <div className="rounded-xl border border-white/5 bg-[#0F0F0F] p-4 text-sm text-white/55" data-testid="waiting-client-proposal">
          Waiting for the customer to propose a time. They'll do this from their My Intake page.
        </div>
      )}

      {clientProposedNow && (
        <div className="rounded-xl border border-purple-500/25 bg-purple-500/[0.03] p-4 space-y-3" data-testid="client-proposed-card">
          <div className="text-[10px] tracking-[0.22em] uppercase text-purple-300">Client requested this time</div>
          <div className="font-display text-lg">
            {new Date(current.scheduled_at).toLocaleString()} · {current.duration_min} min
          </div>
          {current.note && <div className="text-sm text-white/60 italic">"{current.note}"</div>}
        </div>
      )}

      {adminCounteredNow && (
        <div className="rounded-xl border border-blue-500/25 bg-blue-500/[0.03] p-4 space-y-3" data-testid="you-countered-card">
          <div className="text-[10px] tracking-[0.22em] uppercase text-blue-300">You suggested</div>
          <div className="font-display text-lg">
            {new Date(current.scheduled_at).toLocaleString()} · {current.duration_min} min
          </div>
          {current.note && <div className="text-sm text-white/60 italic">"{current.note}"</div>}
          <div className="text-xs text-white/45">Waiting for the customer to accept or send another time.</div>
        </div>
      )}

      {isConfirmed && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.03] p-4 space-y-3" data-testid="confirmed-summary-card">
          <div className="text-[10px] tracking-[0.22em] uppercase text-emerald-300">Confirmed</div>
          <div className="font-display text-lg">
            {new Date(intake.scheduled_at).toLocaleString()} · {intake.scheduled_duration_min || 30} min
          </div>
          <div className="mt-2 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-[10px] text-white/50">Zoom / Meet URL</Label>
              <Input value={zoom} onChange={(e) => setZoom(e.target.value)}
                data-testid="zoom-url-input"
                placeholder="https://zoom.us/j/..."
                className="bg-[#0A0A0A] border-white/10 text-xs mt-1"/>
            </div>
            <Button onClick={generateZoom} disabled={saving === "zoom-gen"}
              variant="outline" data-testid="generate-zoom-btn"
              className="rounded-full bg-transparent border-white/15 hover:bg-white/5 text-xs h-9">
              <Sparkles size={11} className="mr-1"/>{saving === "zoom-gen" ? "Generating..." : "Auto-generate"}
            </Button>
            <Button onClick={attachZoomOnly} disabled={saving === "zoom" || !zoom.trim()}
              data-testid="attach-zoom-btn"
              className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] text-xs h-9 disabled:opacity-40">
              <Video size={11} className="mr-1"/>{saving === "zoom" ? "Sending..." : "Attach & email"}
            </Button>
          </div>
        </div>
      )}

      {/* Action row: accept-proposal (when client proposed) + counter-propose form */}
      {clientProposedNow && (
        <div className="rounded-xl border border-white/5 bg-[#0F0F0F] p-4 space-y-3" data-testid="accept-or-counter-card">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#FF8A00]">Accept the client's time</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <div>
              <Label className="text-[10px] text-white/50">Zoom / Meet URL (leave blank to auto-generate)</Label>
              <Input value={zoom} onChange={(e) => setZoom(e.target.value)}
                data-testid="accept-zoom-input"
                placeholder="Blank → uses Zoom API or your default URL"
                className="bg-[#0A0A0A] border-white/10 text-xs mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] text-white/50">Note in confirmation email (optional)</Label>
              <Input value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                data-testid="accept-note-input"
                placeholder="One line the customer sees"
                className="bg-[#0A0A0A] border-white/10 text-xs mt-1"/>
            </div>
          </div>
          <Button onClick={acceptClientProposal} disabled={saving === "accept"}
            data-testid="accept-proposal-btn"
            className="w-full rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
            <CheckCircle2 size={13} className="mr-1.5"/>
            {saving === "accept" ? "Confirming..." : "Accept & confirm"}
          </Button>
        </div>
      )}

      {(clientProposedNow || nothingYet) && (
        <div className="rounded-xl border border-white/5 bg-[#0F0F0F] p-4 space-y-3" data-testid="counter-propose-card">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#FF8A00]">Suggest a different time</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-[10px] text-white/50">Date &amp; time</Label>
              <Input type="datetime-local" value={counterDt}
                onChange={(e) => setCounterDt(e.target.value)}
                data-testid="counter-datetime"
                className="bg-[#0A0A0A] border-white/10 text-xs mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] text-white/50">Duration (min)</Label>
              <Input type="number" min={15} max={240} step={5}
                value={counterDuration}
                onChange={(e) => setCounterDuration(e.target.value)}
                data-testid="counter-duration"
                className="bg-[#0A0A0A] border-white/10 text-xs mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] text-white/50">Note (optional)</Label>
              <Input value={counterNote} onChange={(e) => setCounterNote(e.target.value)}
                placeholder="Why this time..."
                data-testid="counter-note"
                className="bg-[#0A0A0A] border-white/10 text-xs mt-1"/>
            </div>
          </div>
          <Button onClick={sendCounter} disabled={saving === "counter" || !counterDt}
            data-testid="counter-propose-btn"
            variant="outline"
            className="w-full rounded-full bg-transparent border-white/15 hover:bg-white/5">
            <Send size={13} className="mr-1.5"/>
            {saving === "counter" ? "Sending..." : "Send counter-proposal"}
          </Button>
        </div>
      )}

      {zoomStatus && !zoomStatus.configured && !zoomStatus.has_default_url && (
        <div className="text-xs text-yellow-300/80 bg-yellow-500/5 border border-yellow-500/20 rounded-lg px-3 py-2">
          Heads up — Zoom API isn't configured and you don't have a default meeting URL either. Auto-generate will fail until you set one in Admin → Settings.
        </div>
      )}
    </div>
  );
}



function NotesSection({ intake, onDone }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [editText, setEditText] = useState("");
  const notes = intake.notes || [];

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/intake/responses/${intake.intake_id}/notes`, { text: text.trim() });
      toast.success("Note added");
      setText("");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to add note");
    } finally { setSaving(false); }
  };

  const saveEdit = async (note_id) => {
    if (!editText.trim()) return;
    try {
      await api.patch(`/admin/intake/responses/${intake.intake_id}/notes/${note_id}`, { text: editText.trim() });
      toast.success("Note updated");
      setEditingId(""); setEditText("");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to update note");
    }
  };

  const remove = async (note_id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/admin/intake/responses/${intake.intake_id}/notes/${note_id}`);
      toast.success("Note deleted");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to delete");
    }
  };

  const markStale = async () => {
    const nextStale = !intake.is_stale;
    try {
      await api.post(`/admin/intake/responses/${intake.intake_id}/mark-stale`, { is_stale: nextStale });
      toast.success(nextStale ? "Intake flagged — client will re-review on next booking" : "Intake un-flagged");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="glass rounded-2xl p-5 md:p-6 border border-white/6" data-testid="intake-notes">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00]">CRM notes</div>
          <div className="text-white/80 mt-1 text-sm">Private call log — the client never sees these.</div>
        </div>
        <Button
          onClick={markStale}
          variant="outline"
          data-testid="intake-mark-stale"
          className={`rounded-full bg-transparent border-white/15 text-xs ${intake.is_stale ? "text-[#FF8A00] border-[#FF8A00]/40" : "text-white/60"}`}
        >
          {intake.is_stale ? "Un-flag intake" : "Flag for re-review"}
        </Button>
      </div>

      <div className="mt-4 space-y-2">
        <Textarea rows={3} value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a private note. Timestamped and attributed to you."
          data-testid="intake-note-input"
          className="bg-[#0A0A0A] border-white/10 text-sm" />
        <div className="flex justify-end">
          <Button onClick={add} disabled={!text.trim() || saving}
            data-testid="intake-note-add"
            className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-40">
            <Plus size={13} className="mr-1.5"/> {saving ? "Saving..." : "Add note"}
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {notes.length === 0 && (
          <div className="text-sm text-white/40 border border-white/5 rounded-xl p-4 bg-[#0F0F0F]">
            No notes yet — the first one you add appears here with a timestamp.
          </div>
        )}
        {notes.map((n) => (
          <div key={n.note_id} data-testid={`intake-note-${n.note_id}`}
            className="rounded-xl border border-white/5 bg-[#0F0F0F] p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
              <div className="flex items-center gap-2 text-xs text-white/60">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center text-[10px] font-semibold">
                  {(n.author_name || "A")[0].toUpperCase()}
                </div>
                <span>{n.author_name}</span>
                <span className="text-white/30">·</span>
                <span className="text-white/40">{new Date(n.created_at).toLocaleString()}</span>
                {n.updated_at && n.updated_at !== n.created_at && (
                  <span className="text-[10px] text-white/30 italic">(edited)</span>
                )}
              </div>
              <div className="flex gap-1">
                {editingId === n.note_id ? (
                  <>
                    <Button size="sm" onClick={() => saveEdit(n.note_id)}
                      data-testid={`intake-note-save-${n.note_id}`}
                      className="h-7 rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] text-xs">Save</Button>
                    <Button size="sm" variant="outline"
                      onClick={() => { setEditingId(""); setEditText(""); }}
                      className="h-7 rounded-full bg-transparent border-white/15 text-xs">Cancel</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="ghost"
                      onClick={() => { setEditingId(n.note_id); setEditText(n.text); }}
                      data-testid={`intake-note-edit-${n.note_id}`}
                      className="h-7 rounded-full text-white/50 hover:text-white text-xs px-3">
                      <Pencil size={11} className="mr-1"/>Edit
                    </Button>
                    <Button size="sm" variant="ghost"
                      onClick={() => remove(n.note_id)}
                      data-testid={`intake-note-delete-${n.note_id}`}
                      className="h-7 rounded-full text-white/40 hover:text-[#E52E71] text-xs px-3">
                      <Trash2 size={11} className="mr-1"/>Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
            {editingId === n.note_id ? (
              <Textarea rows={3} value={editText}
                onChange={(e) => setEditText(e.target.value)}
                data-testid={`intake-note-editor-${n.note_id}`}
                className="bg-[#0A0A0A] border-white/10 text-sm" />
            ) : (
              <div className="text-sm text-white/85 whitespace-pre-wrap">{n.text}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SendLinkCard({ intake, disabled, onDone }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const sent = intake.status !== "paid";
  const send = async () => {
    setSending(true);
    try {
      await api.post(`/admin/intake/responses/${intake.intake_id}/send-link`, { message });
      toast.success("Scheduling link emailed");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to send link");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="rounded-xl border border-white/5 bg-[#0F0F0F] p-4 space-y-3" data-testid="send-link-card">
      <div className="flex items-center gap-2">
        <Send size={14} className="text-[#FF8A00]"/>
        <div className="font-display text-sm">1. Send scheduling link</div>
        {sent && <CheckCircle2 size={13} className="text-emerald-400 ml-auto"/>}
      </div>
      <p className="text-[11px] text-white/50">Emails the customer your GHL calendar URL with an intake-specific tag.</p>
      <Textarea rows={2} value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Optional note (e.g. 'Reviewed your intake — pick any 60-min slot next week.')"
        data-testid="send-link-message"
        className="bg-[#0A0A0A] border-white/10 text-xs" />
      <Button onClick={send} disabled={disabled || sending}
        data-testid="send-link-btn"
        className="w-full rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-40">
        <Send size={13} className="mr-1.5" />
        {sending ? "Sending..." : sent ? "Resend link" : "Send link"}
      </Button>
      {intake.link_sent_at && (
        <div className="text-[10px] text-white/40">Last sent: {new Date(intake.link_sent_at).toLocaleString()}</div>
      )}
    </div>
  );
}

function RequestedSlotCard({ intake, onDone }) {
  const [dt, setDt] = useState(intake.requested_slot ? isoToLocal(intake.requested_slot) : "");
  const [duration, setDuration] = useState(intake.requested_duration_min || 60);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!dt) { toast.error("Pick a date and time"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/intake/responses/${intake.intake_id}/set-requested-slot`, {
        scheduled_at: new Date(dt).toISOString(),
        duration_min: parseInt(duration, 10) || 60,
      });
      toast.success("Requested slot saved");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl border border-white/5 bg-[#0F0F0F] p-4 space-y-3" data-testid="requested-slot-card">
      <div className="flex items-center gap-2">
        <CalendarClock size={14} className="text-[#FF8A00]"/>
        <div className="font-display text-sm">2. Slot customer picked</div>
      </div>
      <p className="text-[11px] text-white/50">
        {intake.slot_source === "ghl_webhook"
          ? "Auto-filled from GHL webhook. Edit only if you need to override."
          : "Type the date/time they picked in GHL (or wait for the webhook to fill it)."}
      </p>
      <div>
        <Label className="text-[10px] text-white/50">Date &amp; time</Label>
        <Input type="datetime-local" value={dt}
          onChange={(e) => setDt(e.target.value)}
          data-testid="requested-slot-datetime"
          className="bg-[#0A0A0A] border-white/10 text-xs mt-1" />
      </div>
      <div>
        <Label className="text-[10px] text-white/50">Duration (min)</Label>
        <Input type="number" min={15} max={240} step={5} value={duration}
          onChange={(e) => setDuration(e.target.value)}
          data-testid="requested-slot-duration"
          className="bg-[#0A0A0A] border-white/10 text-xs mt-1" />
      </div>
      <Button onClick={save} disabled={saving}
        data-testid="requested-slot-save"
        className="w-full rounded-full bg-transparent border border-white/10 hover:bg-white/5">
        {saving ? "Saving..." : "Save requested slot"}
      </Button>
    </div>
  );
}

function ConfirmCard({ intake, onDone }) {
  const [dt, setDt] = useState(
    intake.scheduled_at ? isoToLocal(intake.scheduled_at)
      : intake.requested_slot ? isoToLocal(intake.requested_slot) : "");
  const [duration, setDuration] = useState(
    intake.scheduled_duration_min || intake.requested_duration_min || 60);
  const [zoom, setZoom] = useState(intake.meeting_url || intake.requested_meeting_url || "");
  const [note, setNote] = useState(intake.admin_note || "");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const canConfirm = ["slot_requested", "confirmed", "link_sent"].includes(intake.status);

  const generateZoom = async () => {
    if (!dt) { toast.error("Pick a date and time first — Zoom needs the start time"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/admin/intake/responses/${intake.intake_id}/zoom/generate`,
        {
          scheduled_at: new Date(dt).toISOString(),
          duration_min: parseInt(duration, 10) || 60,
          topic: `${intake.session_type_title} — ${intake.name || ""}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        }
      );
      setZoom(data.join_url || "");
      if (data.source === "default_url") {
        toast.success("Filled with your default meeting URL");
      } else {
        toast.success("Zoom meeting created — customer gets an invite from Zoom");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to generate meeting");
    } finally {
      setGenerating(false);
    }
  };

  const confirm = async () => {
    if (!dt) { toast.error("Pick the final date and time"); return; }
    if (!zoom.trim()) { toast.error("Paste a Zoom / Meet URL"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/intake/responses/${intake.intake_id}/confirm`, {
        scheduled_at: new Date(dt).toISOString(),
        duration_min: parseInt(duration, 10) || 60,
        zoom_url: zoom.trim(),
        admin_note: note.trim(),
      });
      toast.success("Confirmed — customer emailed");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to confirm");
    } finally { setSaving(false); }
  };
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${
      intake.status === "confirmed"
        ? "border-emerald-500/30 bg-emerald-500/[0.03]"
        : "border-white/5 bg-[#0F0F0F]"
    }`} data-testid="confirm-card">
      <div className="flex items-center gap-2">
        <Video size={14} className="text-[#FF8A00]"/>
        <div className="font-display text-sm">3. Approve &amp; attach Zoom</div>
        {intake.status === "confirmed" && <CheckCircle2 size={13} className="text-emerald-400 ml-auto"/>}
      </div>
      <p className="text-[11px] text-white/50">Confirms the slot, attaches the meeting URL, and emails the customer.</p>
      <div>
        <Label className="text-[10px] text-white/50">Final date &amp; time</Label>
        <Input type="datetime-local" value={dt}
          onChange={(e) => setDt(e.target.value)}
          data-testid="confirm-datetime"
          className="bg-[#0A0A0A] border-white/10 text-xs mt-1" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-white/50">Duration</Label>
          <Input type="number" min={15} max={240} step={5} value={duration}
            onChange={(e) => setDuration(e.target.value)}
            data-testid="confirm-duration"
            className="bg-[#0A0A0A] border-white/10 text-xs mt-1" />
        </div>
      </div>
      <div>
        <Label className="text-[10px] text-white/50">Zoom / Meet URL</Label>
        <div className="flex gap-2 mt-1">
          <Input value={zoom} onChange={(e) => setZoom(e.target.value)}
            placeholder="https://zoom.us/j/..."
            data-testid="confirm-zoom"
            className="bg-[#0A0A0A] border-white/10 text-xs flex-1" />
          <Button type="button" onClick={generateZoom} disabled={generating}
            variant="outline" size="sm"
            data-testid="generate-zoom-btn"
            className="rounded-full bg-transparent border-white/15 hover:bg-white/5 text-xs h-9 whitespace-nowrap"
            title="Auto-generate a Zoom meeting for this booking">
            <Sparkles size={11} className="mr-1"/> {generating ? "Creating..." : "Auto-generate"}
          </Button>
        </div>
      </div>
      <div>
        <Label className="text-[10px] text-white/50">Note (optional)</Label>
        <Textarea rows={2} value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="confirm-note"
          placeholder="One line included in the confirmation email"
          className="bg-[#0A0A0A] border-white/10 text-xs mt-1" />
      </div>
      <Button onClick={confirm} disabled={!canConfirm || saving}
        data-testid="confirm-btn"
        className="w-full rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-40">
        <Sparkles size={13} className="mr-1.5" />
        {saving ? "Confirming..."
          : intake.status === "confirmed" ? "Re-confirm & re-email"
          : "Confirm & email"}
      </Button>
    </div>
  );
}

function isoToLocal(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function formatValue(v) {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ") || "—";
  return String(v);
}
